import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { Controller, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { handleDropDowns } from "../../config/dropDownHelpers";
import { FaTrash } from "react-icons/fa";
import { threeDecimal, twoDecimal } from "../../config/helperFunctions";
import { GiCutDiamond } from "react-icons/gi";
import { MdPhotoCamera } from "react-icons/md";
import StonePopUp from "./StonePopUp";
import DiamondPopUpNew from "./DiamondPopUpNew";
import LabelItemImagesPopup from "./LabelItemImagesPopup";
import CreatableSelect from "react-select/creatable";
import { selectStyles } from "../NewInvoice/InvoiceItemForm";
const LabelStockItemForm = ({
  control,
  labelledStockFields,
  rowindex,
  register,
  errors,
  watch,
  getValues,
  firstFieldRef,
  onDelete,
  setValue,
  fieldId,
  registerFirstField,
  cardHeightClass = "h-[40vh] sm:h-[40vh]",
  showDelete = true,
  mode = "",
  setImagesTouched, // 👈 ADD
}) => {
  const categoriesFromState = useSelector(
    (state) => state.categories?.categories,
  );
  const productsFromState = useSelector((state) => state.products?.products);
  const vendorsFromState = useSelector((state) => state.vendors?.vendors);
  const designFromState = useSelector((state) => state.designs?.designs);
  const purityFromState = useSelector((state) => state.purities?.purities);
  const branchesFromState = useSelector((state) => state.branches?.branches);

  const [openStoneModal, setOpenStoneModal] = useState(false);
  const [openDiamondModal, setOpenDiamondModal] = useState(false);
  const [openImagesModal, setOpenImagesModal] = useState(false); // 👈 NEW

  const stoneBtnRef = useRef(null);
  const diamondBtnRef = useRef(null);

  const category = watch(`stockEntries.${rowindex}.category`);
  const product = watch(`stockEntries.${rowindex}.product`);
  const totalWt = watch(`stockEntries.${rowindex}.totalWt`);
  const packingWt = watch(`stockEntries.${rowindex}.packingWt`);
  const stoneWt = watch(`stockEntries.${rowindex}.stoneWt`);
  const diamondWt = watch(`stockEntries.${rowindex}.diamondWt`);

  const stonesFieldName = `stockEntries.${rowindex}.stones`;
  const diamondsFieldName = `stockEntries.${rowindex}.diamonds`;
  const imagesFieldName = `stockEntries.${rowindex}.images`; // 👈 NEW

  useEffect(() => {
    register(stonesFieldName);
    register(diamondsFieldName);
    register(imagesFieldName);
  }, [register, stonesFieldName, diamondsFieldName, imagesFieldName]);

  const stonesList = useWatch({ control, name: stonesFieldName }) || [];
  const diamondsList = useWatch({ control, name: diamondsFieldName }) || [];
  const imagesList = useWatch({ control, name: imagesFieldName }) || []; // 👈 NEW

  const formRef = useRef(null);

  // Ctrl+Delete to delete current card
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showDelete) return;
      if (e.ctrlKey && e.key === "Delete") {
        if (
          formRef.current &&
          formRef.current.contains(document.activeElement)
        ) {
          e.preventDefault();
          onDelete?.();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onDelete, showDelete]);

  /** Diamonds always carat → keep diamondWt (grams) in sync from popup list */
  // useEffect(() => {
  //   const grams = (Array.isArray(diamondsList) ? diamondsList : [])
  //     .filter(d => String(d?.diaPopupDeduct || "").toLowerCase() === "deduct")
  //     .reduce((acc, d) => acc + ((Number(d?.diaPopupWt) || 0) / 5), 0);
  //   setValue(`stockEntries.${rowindex}.diamondWt`, threeDecimal(grams), {
  //     shouldDirty: true,
  //     shouldValidate: false,
  //   });
  // }, [diamondsList, rowindex, setValue]);

  // 🧮 derive gross & net (all in grams). diamondWt already grams (auto or manual).
  useEffect(() => {
    const gross = ((+totalWt || 0) - (+packingWt || 0)).toFixed(3);
    const net = (
      (+totalWt || 0) -
      (+packingWt || 0) -
      (+stoneWt || 0) -
      (+diamondWt || 0) / 5
    ).toFixed(3);

    setValue(`stockEntries.${rowindex}.grossWt`, gross);
    setValue(`stockEntries.${rowindex}.netWt`, net);
  }, [totalWt, packingWt, stoneWt, diamondWt, setValue, rowindex]);

  // return focus to trigger button after modal closes
  useEffect(() => {
    if (!openStoneModal && stoneBtnRef.current) stoneBtnRef.current.focus();
  }, [openStoneModal]);
  useEffect(() => {
    if (!openDiamondModal && diamondBtnRef.current)
      diamondBtnRef.current.focus();
  }, [openDiamondModal]);

  return (
    <>
      <motion.div
        ref={formRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: rowindex * 0.05 }}
        id={`stock-card-${fieldId}`}
        data-field-id={fieldId}
        className={`stock-card ${cardHeightClass} hide-scrollbar-inv-items snap-start px-2  pb-2 rounded-lg border border-gray-200 hover:border-gray-300 shadow-lg mb-0 bg-white relative overflow-y-auto`}
      >
        {/* Header */}
        <div className="mb-0.5 flex items-center justify-between sticky top-0 bg-white pt-0 pb-1 m-0 z-10">
          <h3 className="text-sm font-semibold text-gray-900">
            #{rowindex + 1} • Stock Entry
          </h3>
          <div className="flex-1 h-px bg-gray-200 mx-2" />
          {showDelete && (
            <button
              type="button"
              tabIndex={-1}
              className="text-gray-500 hover:text-red-600 transition-all duration-200 hover:scale-110"
              title="Delete this item"
              onClick={onDelete}
            >
              <FaTrash className="text-[14px]" />
            </button>
          )}
        </div>

        {/* Fields */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-2">
          {labelledStockFields?.map((item, idx) => {
            const colSpan = [
              "category",
              "product",
              "design",
              "description",
            ].includes(item?.name)
              ? "md:col-span-2"
              : "md:col-span-1";
            const path = `stockEntries.${rowindex}.${item.name}`;
            const rules = { required: false };

            return (
              <div key={idx} className={colSpan}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {item?.label}{" "}
                  {item?.mandatory && <span className="text-red-500">*</span>}
                </label>

                {/* TEXT */}
                {item?.type === "text" && (
                  <>
                    <input
                      {...register(path, rules)}
                      ref={(el) => {
                        register(path, rules).ref(el);
                        if (idx === 0 && firstFieldRef)
                          firstFieldRef.current = el;
                        if (idx === 0 && registerFirstField)
                          registerFirstField(fieldId, el);
                      }}
                      disabled={
                        item.name === "grossWt" || item.name === "netWt"
                      } // keep only computed locks
                      placeholder={item?.placeholder || ""}
                      className="w-full px-3 py-[5px] text-sm border border-gray-300 rounded-md bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
                    />
                    {errors?.stockEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.stockEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </>
                )}

                {/* NUMBER */}
                {item?.type === "number" && (
                  <>
                    <input
                      type="number"
                      {...register(path, rules)}
                      ref={(el) => {
                        register(path, rules).ref(el);
                        if (idx === 0 && firstFieldRef)
                          firstFieldRef.current = el;
                        if (idx === 0 && registerFirstField)
                          registerFirstField(fieldId, el);
                      }}
                      disabled={
                        item.name === "grossWt" || item.name === "netWt"
                      } // diamondWt is ENABLED now
                      placeholder={item?.placeholder || ""}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
                    />
                    {errors?.stockEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.stockEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </>
                )}

                {/* DATE */}
                {item?.type === "date" && (
                  <>
                    <input
                      type="date"
                      {...register(path, rules)}
                      ref={(el) => {
                        register(path, rules).ref(el);
                        if (idx === 0 && firstFieldRef)
                          firstFieldRef.current = el;
                        if (idx === 0 && registerFirstField)
                          registerFirstField(fieldId, el);
                      }}
                      className="w-full px-3 h-8 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
                    />
                    {errors?.stockEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.stockEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </>
                )}

                {/* DROPDOWN */}
                {item?.type === "dropdown" && (
                  <>
                    <Controller
                      name={path}
                      control={control}
                      rules={rules}
                      render={({ field }) => (
                        <Select
                          {...field}
                          ref={(ref) => {
                            field.ref(ref);
                            if (idx === 0 && registerFirstField) {
                              registerFirstField(fieldId, {
                                focus: () => ref?.focus?.(),
                              });
                            }
                          }}
                          options={handleDropDowns({
                            key: item.name,
                            vendors: vendorsFromState,
                            categories: categoriesFromState,
                            products: productsFromState,
                            designs: designFromState,
                            purities: purityFromState,
                            branches: branchesFromState,
                            category,
                            product,
                          })}
                          classNamePrefix="react-select"
                          menuPlacement="bottom"
                          isClearable
                          isSearchable
                          isDisabled={
                            mode == "edit" &&
                            (item?.name == "category" ||
                              item?.name == "product" ||
                              item?.name == "design")
                          }
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              minHeight: "32px",
                              height: "32px",
                              borderColor: state.isFocused
                                ? "#A78BFA"
                                : "#D1D5DB",
                              boxShadow: state.isFocused
                                ? "0 0 0 2px rgba(167,139,250,0.35)"
                                : "none",
                              fontSize: "12px",
                              backgroundColor: "white",
                            }),
                            indicatorsContainer: (base) => ({
                              ...base,
                              height: "30px",
                            }),
                            dropdownIndicator: (base) => ({
                              ...base,
                              padding: "4px",
                            }),
                            clearIndicator: (base) => ({
                              ...base,
                              padding: "4px",
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: "0 6px",
                              height: "30px",
                            }),
                            input: (base) => ({
                              ...base,
                              margin: 0,
                              padding: 0,
                            }),
                            menu: (base) => ({ ...base, fontSize: "12px" }),
                            menuList: (base) => ({
                              ...base,
                              maxHeight: "140px",
                              overflowY: "auto",
                            }),
                            singleValue: (base) => ({
                              ...base,
                              fontSize: "12px",
                            }),
                          }}
                        />
                      )}
                    />
                    {errors?.stockEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.stockEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </>
                )}

                {item?.type === "creatable-dropdown" && (
                  <>
                    <Controller
                      name={path}
                      control={control}
                      rules={rules}
                      render={({ field }) => (
                        <CreatableSelect
                          {...field}
                          options={handleDropDowns({
                            key: item.name,
                            categories: categoriesFromState,
                            products: productsFromState,
                            designs: designFromState,
                            purities: purityFromState,
                            category,
                            product,
                          })}
                          classNamePrefix="react-select"
                          menuPlacement="bottom"
                          isClearable
                          isSearchable
                          onCreateOption={(inputValue) => {
                            const clean = inputValue.trim();
                            if (!clean) return;

                            // 👇 NEW value – id nahi hai
                            field.onChange({
                              label: clean,
                              value: null,
                              // __isNew__: true, // optional but useful
                            });
                          }}
                          onChange={(opt) => field.onChange(opt ?? null)}
                          styles={selectStyles}
                        />
                      )}
                    />

                    {errors?.stockEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.stockEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </>
                )}

                {/* Stones */}
                {item?.type === "button" && item?.name === "stones" && (
                  <div className="relative w-fit mt-3">
                    <AnimatePresence>
                      {openStoneModal && (
                        <StonePopUp
                          key={`stone-popup-${fieldId}`}
                          setOpenStoneModal={setOpenStoneModal}
                          initialStones={stonesList}
                          onSave={(stones) => {
                            let pcs = 0,
                              amt = 0,
                              wt = 0;
                            for (const s of stones || []) {
                              const p = Number(s?.stnPopupPcs) || 0;
                              const a = Number(s?.stnPopupAmt) || 0;
                              const w =
                                String(
                                  s?.stnPopupDeduct || "",
                                ).toLowerCase() === "deduct"
                                  ? (
                                      s?.unit?.value ||
                                      s?.unit ||
                                      ""
                                    ).toLowerCase() === "gram"
                                    ? Number(s?.stnPopupWt) || 0
                                    : (Number(s?.stnPopupWt) || 0) / 5
                                  : 0;
                              pcs += p;
                              amt += a;
                              wt += w;
                            }
                            setValue(`stockEntries.${rowindex}.stones`, stones);
                            setValue(
                              `stockEntries.${rowindex}.stonePcs`,
                              String(pcs),
                            );
                            setValue(
                              `stockEntries.${rowindex}.totalStoneAmount`,
                              twoDecimal(amt),
                            );
                            setValue(
                              `stockEntries.${rowindex}.stoneWt`,
                              threeDecimal(wt),
                            );
                            setOpenStoneModal(false);
                          }}
                          onClose={() => setOpenStoneModal(false)}
                        />
                      )}
                    </AnimatePresence>

                    <motion.div
                      className="cursor-pointer text-violet-600"
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <button
                        type="button"
                        ref={stoneBtnRef}
                        onClick={() => setOpenStoneModal(true)}
                        aria-haspopup="dialog"
                        aria-expanded={openStoneModal}
                        aria-controls={`stone-dialog-${fieldId}`}
                      >
                        <GiCutDiamond size={20} />
                      </button>
                    </motion.div>
                  </div>
                )}

                {/* Diamonds (ALWAYS CARAT) */}
                {item?.type === "button" && item?.name === "diamonds" && (
                  <div className="relative w-fit mt-3">
                    {openDiamondModal && (
                      <DiamondPopUpNew
                        key={`diamond-popup-${fieldId}`}
                        setOpenDiamondModal={setOpenDiamondModal}
                        initialDiamonds={diamondsList}
                        itemIndex={rowindex}
                        onSave={(diamonds) => {
                          let pcs = 0,
                            amt = 0,
                            grams = 0;
                          for (const d of diamonds || []) {
                            const p = Number(d?.diaPopupPcs) || 0;
                            const a = Number(d?.diaPopupAmt) || 0;
                            const g =
                              String(d?.diaPopupDeduct || "").toLowerCase() ===
                              "deduct"
                                ? Number(d?.diaPopupWt) || 0
                                : 0;
                            pcs += p;
                            amt += a;
                            grams += g;
                          }
                          setValue(
                            `stockEntries.${rowindex}.diamonds`,
                            diamonds,
                          );
                          setValue(
                            `stockEntries.${rowindex}.diamondPcs`,
                            String(pcs),
                          );
                          setValue(
                            `stockEntries.${rowindex}.totalDiamondAmount`,
                            twoDecimal(amt),
                          );
                          setValue(
                            `stockEntries.${rowindex}.diamondWt`,
                            threeDecimal(grams),
                          ); // grams
                          setOpenDiamondModal(false);
                        }}
                        onClose={() => setOpenDiamondModal(false)}
                      />
                    )}

                    <motion.div
                      className="cursor-pointer text-violet-600"
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <button
                        type="button"
                        ref={diamondBtnRef}
                        onClick={() => setOpenDiamondModal(true)}
                        aria-haspopup="dialog"
                        aria-expanded={openDiamondModal}
                        aria-controls={`diamond-dialog-${fieldId}`}
                      >
                        <GiCutDiamond size={20} />
                      </button>
                    </motion.div>
                  </div>
                )}

                {/* Images */}
                {/* Images */}
                {item?.type === "button" && item?.name === "images" && (
                  <div className="relative w-fit mt-3">
                    <AnimatePresence>
                      {openImagesModal && (
                        <LabelItemImagesPopup
                          key={`images-popup-${fieldId}`}
                          open={openImagesModal}
                          initialImages={imagesList}
                          onSave={(images) => {
                            setImagesTouched(true);
                            // form state me store karenge; yeh payload me nahi jaa raha abhi
                            setValue(imagesFieldName, images, {
                              shouldDirty: true,
                              shouldValidate: false,
                            });
                            setOpenImagesModal(false);
                          }}
                          onClose={() => setOpenImagesModal(false)}
                        />
                      )}
                    </AnimatePresence>

                    <motion.button
                      type="button"
                      onClick={() => setOpenImagesModal(true)}
                      className="relative flex items-center justify-center bg-gray-100 rounded-md p-1.5 group"
                      animate={{ y: [0, -3, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <MdPhotoCamera
                        size={22}
                        className="text-blue-500 group-hover:text-blue-600 transition-colors"
                      />

                      {Array.isArray(imagesList) && imagesList.length > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] leading-none px-1.5 py-[2px] rounded-full">
                          {imagesList.length}
                        </span>
                      )}
                    </motion.button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>
    </>
  );
};

export default LabelStockItemForm;
